import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Badge,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Empty,
  Form,
  Image,
  Modal,
  Radio,
  Tag,
  Tooltip,
} from 'antd';
import { debounce, filter, forEach, isEmpty, map, toInteger } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import Countdown from 'react-countdown';
import { useHistory, useLocation } from 'react-router-dom';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as CommentIcon } from '../../../../../assets/svg/comment-dots-solid.svg';
import { ReactComponent as FilterIcon } from '../../../../../assets/svg/sliders-solid.svg';
import {
  BREACH_TIME_CRITICAL_THRESHOLD,
  BREACH_TIME_WARNING_THRESHOLD,
  CANCELLATION_TYPE,
  CRITICAL_ORDERS_KEY,
  DATE_FORMATS,
  DELIVERY_TYPE,
  DELIVERY_TYPE_OPTIONS,
  FIRST_ORDER_FLAG,
  FULFILLMENT_STATUS,
  LIMIT,
  NEW_ORDER_EXPANDED_TABLE_ACTIONS_OPTIONS,
  NEW_ORDER_TABLE_ACTIONS_OPTIONS,
  ORDER_STATUS,
  ORDER_TYPE,
  ORDERS_POLLING_INTERVAL,
  ROUTES,
  SORT,
  SORTING,
  TRANSFER_ORDER_STATUS,
  USER_ROLES,
} from '../../../../../common/constants';
import {
  formatDate,
  getRowClassName,
  hasNonEmptyValueObj,
  renderer,
} from '../../../../../common/utils';
import CommonPreview from '../../../../../components/CommonPreview';
import CommonPreviewTable from '../../../../../components/CommonPreviewTable';
import CommonTable from '../../../../../components/CommonTable';
import LoaderComponent from '../../../../../components/LoaderComponent';
import OrderStatusSelect from '../../../../../components/OrderStatusSelect';
import SearchComponent from '../../../../../components/SearchComponent';
import SelectComponent from '../../../../../components/SelectComponent';
import SortDropdown from '../../../../../components/SortDropdown';
import { PINCODES } from '../../../../../components/graphql/Queries';
import useInfiniteScroll from '../../../../../hooks/useInfiniteScroll';
import useOrderAlert from '../../../../../hooks/useOrderAlert';
import AssignDeliveryPartnerModal from '../../../../../modules/orders/modules/new/components/AssignDeliveryPartnerModal';
import { ASSIGN_DELIVERY_PARTNER_V2 } from '../../../../../modules/orders/modules/new/graphql/Mutations';
import UserSelect from '../../../../../modules/payments/modules/endUser/components/UserSelect';
import AllFilterOrderModal from '../../all/components/AllFilterOrderModal';
import AllOrderItemModal from '../../all/components/AllOrderItemModal';
import AllOrderList from '../../all/components/AllOrderList';
import { ORDER_ITEMS, ORDERS, ORDERS_SELECT } from '../../all/graphql/Queries';
import { LIBRARY_USERS } from '../graphql/Queries';

const { RangePicker } = DatePicker;
const { Group } = Radio;

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialNewDeliveryFilter = {
  limit: LIMIT,
  search: '',
  skip: 0,
  showPlacedOrderCount: true,
  showConfirmedOrderCount: true,
};

const initialNewDeliverySort = {
  field: 'createdAt',
  order: 'DESC',
};

const initialTableFilter = {
  skip: 0,
  limit: LIMIT,
  search: '',
};

const initialTableSort = {
  field: 'createdAt',
  order: 'DESC',
};

let filterScrollDebounceJob = null;

const RiderManagementTable = ({ exportFilter, setExportFilter }) => {
  const isAssignDelPartnerTab = true;
  const location = useLocation();
  const [form] = Form.useForm();
  const [delayForm] = Form.useForm();
  const [assignDelPartnerForm] = Form.useForm();
  const {
    dispatch,
    state: {
      isDesktop,
      orderCount,
      commonPermissions: {
        isUserPermission,
        isToyPermission,
        isDeliveryPartnerPermission,
        isLibraryPermission,
      },
    },
  } = useContext(AppContext);

  const sort = [
    {
      label: SORTING?.ORDER_ID_A_Z?.label,
      value: SORTING?.ORDER_ID_A_Z?.value,
      field: SORTING?.ORDER_ID_A_Z?.field,
      sort: SORTING?.ORDER_ID_A_Z?.sort,
    },
    {
      label: SORTING?.ORDER_ID_Z_A?.label,
      value: SORTING?.ORDER_ID_Z_A?.value,
      field: SORTING?.ORDER_ID_Z_A?.field,
      sort: SORTING?.ORDER_ID_Z_A?.sort,
    },
    {
      label: SORTING?.TOTAL_ORDERS_COUNT_A_Z?.label,
      value: SORTING?.TOTAL_ORDERS_COUNT_A_Z?.value,
      field: SORTING?.TOTAL_ORDERS_COUNT_A_Z?.field,
      sort: SORTING?.TOTAL_ORDERS_COUNT_A_Z?.sort,
    },
    {
      label: SORTING?.TOTAL_ORDERS_COUNT_Z_A?.label,
      value: SORTING?.TOTAL_ORDERS_COUNT_Z_A?.value,
      field: SORTING?.TOTAL_ORDERS_COUNT_Z_A?.field,
      sort: SORTING?.TOTAL_ORDERS_COUNT_Z_A?.sort,
    },
  ];

  const history = useHistory();
  const queryParams = new URLSearchParams(location?.search);
  const orderId = queryParams?.get('orderId') || '';
  const [orderList, setOrderList] = useState([]);
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const orderSearchTermRef = useRef(orderSearchTerm);
  const [isEmptyOrderList, setIsEmptyOrderList] = useState(false);
  const [order, setOrder] = useState({});
  const [orderItem, setOrderItem] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const sortedInfoRef = useRef(null);
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const paginationPropRef = useRef(initialPaginationValue);
  const [isOrderLoading, setIsOrderLoading] = useState(true);
  const [isNotePrompts, setIsNotePrompts] = useState(false);
  const [isAddCommentModalOpen, setIsAddCommentModalOpen] = useState(false);
  const [isAddCommentLoading, setIsAddCommentLoading] = useState(false);
  const [isOrderAction, setIsOrderAction] = useState(false);
  const [isFulfillLoading, setIsFulfillLoading] = useState(false);
  const [isFulfillPrompts, setIsFulfillPrompts] = useState(false);
  const [isReadyLoading, setIsReadyLoading] = useState(false);
  const [isReadyPrompts, setIsReadyPrompts] = useState(false);
  const [isDelayReasonPrompts, setIsDelayReasonPrompts] = useState(false);
  const [isConfirmPrompts, setIsConfirmPrompts] = useState(false);
  const [isReasonPrompt, setIsReasonPrompt] = useState(false);

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [nestedData, setNestedData] = useState({});
  const [isLoading, setIsLoading] = useState({});
  const [isDisabled, setIsDisabled] = useState(false);

  const [filterSearch, setFilterSearch] = useState('');
  const [filterList, setFilterList] = useState([]);
  const [whereFilter, setWhereFilter] = useState();
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterTags, setFilterTags] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterIndex, setFilterIndex] = useState(null);
  const [finalWhereFilters, setFinalWhereFilters] = useState();
  const finalWhereFiltersRef = useRef(null);
  const [finalFilterTags, setFinalFilterTags] = useState(null);
  const [filterIsEnd, setFilterIsEnd] = useState(false);
  const [isEmptyFilterList, setIsEmptyFilterList] = useState(false);
  const [visible, setVisible] = useState(false);
  const [toyImages, setToyImages] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [toyPreviewImage, setToyPreviewImage] = useState();
  const [isContentMasterModalOpen, setIsContentMasterModalOpen] = useState(
    false,
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isDeliveryPartnerLoading, setIsDeliveryPartnerLoading] = useState(
    false,
  );
  const [isDeliveryPartnerPrompts, setIsDeliveryPartnerPrompts] = useState(
    false,
  );
  const [deliveryPartnerData, setDeliveryPartnerData] = useState({});
  const [isDeliveryPartnerModalOpen, setDeliveryPartnerModalOpen] = useState(
    false,
  );
  const [isAllFilterModalOpen, setIsAllFilterModalOpen] = useState(false);
  const [isAllOrderItemModalOpen, setIsAllOrderItemModalOpen] = useState(false);
  const [isAllSortPopoverOpen, setIsAllSortPopoverOpen] = useState(false);
  const [isFetchMoreOrderLoading, setIsFetchMoreOrderLoading] = useState(false);
  const [thresholdData, setThresholdData] = useState({});

  const tempComment = useRef(null);
  const tempUnfulfilledReason = useRef(null);
  const [isActive, setIsActive] = useState(true);
  const isPollingRef = useRef(false);
  const statusFilter = isAssignDelPartnerTab
    ? [ORDER_STATUS?.READY_TO_SHIP?.value, ORDER_STATUS?.CONFIRMED?.value]
    : [ORDER_STATUS?.PLACED?.value];
  const [orders] = useLazyQuery(ORDERS, {
    onCompleted: (response) => {
      const criticalThreshold = toInteger(
        response?.orders?.configs?.find(
          (config) => config?.key === BREACH_TIME_CRITICAL_THRESHOLD,
        )?.value,
      );
      const warningThreshold = toInteger(
        response?.orders?.configs?.find(
          (config) => config?.key === BREACH_TIME_WARNING_THRESHOLD,
        )?.value,
      );

      setThresholdData({
        criticalThreshold,
        warningThreshold,
      });
      if (isAssignDelPartnerTab) {
        dispatch({
          type: 'SET_ORDER_COUNT',
          data: {
            ...orderCount,
            confirmedOrderCount: response?.orders?.count,
            remainingConfirmedOrderCount: response?.orders?.confirmedOrderCount,
          },
        });
      } else {
        dispatch({
          type: 'SET_ORDER_COUNT',
          data: {
            ...orderCount,
            newOrderCount: response?.orders?.count,
            placedOrderCount: response?.orders?.placedOrderCount,
          },
        });
      }
      if (isDesktop) {
        setOrderList([...response?.orders?.data]);
      } else {
        const newOrders = response?.orders?.data || [];
        const existingIds = new Set(
          orderList?.map((currentOrder) => currentOrder?.orderNumber),
        );
        const uniqueNewOrders = newOrders?.filter(
          (currentOrder) => !existingIds?.has(currentOrder.orderNumber),
        );

        if (isPollingRef?.current) {
          setOrderList([...uniqueNewOrders, ...orderList]);
          isPollingRef.current = false;
        } else {
          setOrderList([...orderList, ...uniqueNewOrders]);
        }
      }
      if (
        response?.orders?.count === 0 &&
        initialPaginationValue?.total === 0
      ) {
        setIsEmptyOrderList(true);
      } else {
        setIsEmptyOrderList(false);
      }

      if (response?.orders?.data?.length < LIMIT) {
        setIsActive(false);
      }

      const pagination = {
        ...paginationProp,
        defaultPageSize: LIMIT,
        total: response?.orders?.count,
      };
      setPaginationProp(pagination);
      paginationPropRef.current = pagination;
      setIsOrderLoading(false);
      setIsFetchMoreOrderLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  const handleSort = (values) => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsOrderLoading(true);
    setIsAllSortPopoverOpen(false);
    setIsActive(true);
    setOrderList([]);
    const filteredSort = sort?.filter((item) => item?.value === values?.sort);
    if (filteredSort?.[0]?.field) {
      setSortedInfo({
        ...sortedInfo,
        field: filteredSort?.[0]?.field,
        columnKey: filteredSort?.[0]?.field,
        order: filteredSort?.[0]?.sort === SORT?.ASC ? 'ascend' : 'descend',
        column: filteredSort?.[0]?.field,
      });
    }
    orders({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          orderIds: finalWhereFilters?.orderNumber,
          search: orderSearchTerm,
          deliveryType: finalWhereFilters?.deliveryType,
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFilters?.deliveredOn?.from?.$d,
            to: finalWhereFilters?.deliveredOn?.to?.$d,
          },
          pincodeIds: finalWhereFilters?.pincodes,
        },
        sort: filteredSort?.[0]
          ? {
              field: filteredSort?.[0]?.field,
              order: filteredSort?.[0]?.sort,
            }
          : initialNewDeliverySort,
      },
    });
  };

  const [orderItems] = useLazyQuery(ORDER_ITEMS, {
    fetchPolicy: 'no-cache',
    onError() {},
  });

  const { playNotificationSound } = useOrderAlert();
  const criticalOrdersRef = useRef(new Set());

  useEffect(() => {
    // eslint-disable-next-line no-undef
    const stored = localStorage.getItem(CRITICAL_ORDERS_KEY);
    if (stored) {
      try {
        criticalOrdersRef.current = new Set(JSON.parse(stored));
      } catch (error) {
        criticalOrdersRef.current = new Set();
      }
    }
  }, []);

  const [users, { loading: isUsersLoading }] = useLazyQuery(LIBRARY_USERS, {
    onCompleted: (response) => {
      if (response?.users?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.users?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.users?.data, (item) =>
        optionsCopy?.push({
          label: `${
            item?.firstName || item?.lastName
              ? `${item?.firstName || ''} ${item?.lastName || ''}`
              : '-'
          }`,
          key: item?.id,
        }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });

  const [pincodes, { loading: isPincodesLoading }] = useLazyQuery(PINCODES, {
    onCompleted: (response) => {
      if (response?.pincodes?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.pincodes?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.pincodes?.data, (item) =>
        optionsCopy?.push({
          label: item?.pincode,
          key: item?.id,
        }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });

  const [orderSelect, { loading: isOrderSelectLoading }] = useLazyQuery(
    ORDERS_SELECT,
    {
      onCompleted: (response) => {
        if (response?.orders?.data?.length < LIMIT) {
          setFilterIsEnd(true);
        }
        if (response?.orders?.count === 0) {
          setIsEmptyFilterList(true);
        } else {
          setIsEmptyFilterList(false);
        }
        const optionsCopy = [];
        forEach(response?.orders?.data, (item) =>
          optionsCopy?.push({
            label: item?.orderNumber,
            key: item?.id,
          }),
        );
        setFilterList([...filterList, ...optionsCopy]);
        setFilterLoading(false);
      },
      fetchPolicy: 'network-only',
      onError() {
        setFilterLoading(false);
      },
    },
  );

  const [assignDeliveryPartnerV2] = useMutation(ASSIGN_DELIVERY_PARTNER_V2, {
    onError: () => {},
  });

  const fetchOrders = () => {
    isPollingRef.current = true;
    orders({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          skip:
            (paginationPropRef?.current?.current - 1) *
            (paginationPropRef?.current?.pageSize || 0),
          search: orderSearchTermRef?.current,
          limit: paginationPropRef?.current?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          orderIds: finalWhereFiltersRef?.current?.orderNumber,
          deliveryType: finalWhereFiltersRef?.current?.deliveryType,
          status:
            finalWhereFiltersRef?.current?.status?.length > 0
              ? finalWhereFiltersRef?.current?.status
              : statusFilter,
          dateRange: {
            from: finalWhereFiltersRef?.current?.createdAt?.from?.$d,
            to: finalWhereFiltersRef?.current?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFiltersRef?.current?.deliveredOn?.from?.$d,
            to: finalWhereFiltersRef?.current?.deliveredOn?.to?.$d,
          },
          pincodeIds: finalWhereFiltersRef?.current?.pincodes,
          statusTiming: finalWhereFiltersRef?.current?.statusTiming,
        },
        sort: sortedInfoRef?.current?.column
          ? {
              field: sortedInfoRef?.current?.field,
              order:
                sortedInfoRef?.current?.order === 'ascend'
                  ? SORT?.ASC
                  : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  };

  useEffect(() => {
    fetchOrders();
    const intervalId = setInterval(fetchOrders, ORDERS_POLLING_INTERVAL);

    return () => clearInterval(intervalId);
  }, []);

  const handleOrderScroll = () => {
    if (!isFetchMoreOrderLoading && !isOrderLoading) {
      isPollingRef.current = false;
      const currentLength = orderList?.length;
      setIsFetchMoreOrderLoading(true);
      orders({
        variables: {
          filter: {
            ...initialNewDeliveryFilter,
            skip: currentLength,
            limit: paginationProp?.pageSize || LIMIT,
            orderType: ORDER_TYPE?.NEW,
            orderIds: finalWhereFilters?.orderNumber,
            search: orderSearchTerm,
            deliveryType: finalWhereFilters?.deliveryType,
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : statusFilter,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            deliveredOn: {
              from: finalWhereFilters?.deliveredOn?.from?.$d,
              to: finalWhereFilters?.deliveredOn?.to?.$d,
            },
            pincodeIds: finalWhereFilters?.pincodes,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialNewDeliverySort,
        },
      });
    }
  };

  const mobileConfig = {};

  const infiniteScrollRef = useInfiniteScroll(handleOrderScroll, isActive);

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    paginationPropRef.current = { ...paginationProp, ...pagination };
    setIsOrderLoading(true);
    setSortedInfo(sorter);
    sortedInfoRef.current = sorter;
    setNestedData({});
    setExpandedRowKeys([]);
    orders({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          skip,
          limit: pagination?.pageSize,
          orderType: ORDER_TYPE?.NEW,
          orderIds: finalWhereFilters?.orderNumber,
          search: orderSearchTerm,
          deliveryType: finalWhereFilters?.deliveryType,
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : statusFilter,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFilters?.deliveredOn?.from?.$d,
            to: finalWhereFilters?.deliveredOn?.to?.$d,
          },
          pincodeIds: finalWhereFilters?.pincodes,
          statusTiming: finalWhereFilters?.statusTiming,
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  };

  const isLastItem = (orderIds) => {
    const orderData = nestedData?.[orderIds]?.orderItems?.data || [];
    const processedCount = orderData?.filter(
      (item) =>
        item?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
        item?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
        item?.status === FULFILLMENT_STATUS?.CANCELLED?.value,
    )?.length;

    const totalItems = orderData?.length;

    return processedCount + 1 >= totalItems;
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setOrderSearchTerm(trimValue);
    orderSearchTermRef.current = trimValue;
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    paginationPropRef.current = { ...paginationProp, current: 1, skip: 0 };
    setIsOrderLoading(true);
    setOrderList([]);
    setNestedData({});
    setExpandedRowKeys([]);
    orders({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          orderIds: finalWhereFilters?.orderNumber,
          search: trimValue,
          deliveryType: finalWhereFilters?.deliveryType,
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : statusFilter,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFilters?.deliveredOn?.from?.$d,
            to: finalWhereFilters?.deliveredOn?.to?.$d,
          },
          statusTiming: finalWhereFilters?.statusTiming,
          pincodeIds: finalWhereFilters?.pincodes,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
    setExportFilter({ ...exportFilter, search: trimValue });
  };

  const handleAssignDeliveryPartner = async (rowKeys, deliveryPartner) => {
    setIsDeliveryPartnerLoading(true);
    const response = await assignDeliveryPartnerV2({
      variables: {
        where: {
          id: rowKeys,
        },
        data: {
          deliveryPartnerId: deliveryPartner,
        },
      },
    });
    if (response?.data) {
      setIsOrderLoading(true);
      setDeliveryPartnerModalOpen(false);
      setNestedData({});
      setOrderList([]);
      setExpandedRowKeys([]);
      setSelectedRowKeys([]);
      setIsDeliveryPartnerPrompts(false);
      setDeliveryPartnerData({});
      assignDelPartnerForm.resetFields();
      orders({
        variables: {
          filter: {
            ...initialNewDeliveryFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            orderType: ORDER_TYPE?.NEW,
            orderIds: finalWhereFilters?.orderNumber,
            search: orderSearchTerm,
            deliveryType: finalWhereFilters?.deliveryType,
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : statusFilter,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            deliveredOn: {
              from: finalWhereFilters?.deliveredOn?.from?.$d,
              to: finalWhereFilters?.deliveredOn?.to?.$d,
            },
            statusTiming: finalWhereFilters?.statusTiming,
            pincodeIds: finalWhereFilters?.pincodes,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialNewDeliverySort,
        },
      });
    }
    setIsDeliveryPartnerLoading(false);
  };

  const getFilterData = (confirm) => {
    setIsOrderLoading(true);
    setNestedData({});
    setExpandedRowKeys([]);
    orders({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          search: orderSearchTerm,
          orderType: ORDER_TYPE?.NEW,
          orderIds: whereFilter?.orderNumber,
          deliveryType: whereFilter?.deliveryType,
          status:
            whereFilter?.status?.length > 0
              ? whereFilter?.status
              : statusFilter,
          dateRange: {
            from: whereFilter?.createdAt?.from?.$d,
            to: whereFilter?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: whereFilter?.deliveredOn?.from?.$d,
            to: whereFilter?.deliveredOn?.to?.$d,
          },
          pincodeIds: whereFilter?.pincodes,
          statusTiming: finalWhereFilters?.statusTiming,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFilter });
    finalWhereFiltersRef.current = { ...finalWhereFilters, ...whereFilter };
    setExportFilter({ ...exportFilter, ...finalWhereFilters, ...whereFilter });
    setFinalFilterTags(filterTags);
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    paginationPropRef.current = { ...paginationProp, skip: 0, current: 1 };
    if (confirm) {
      confirm();
    }
  };

  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleReset = (clearFilters, dataIndex, confirm) => {
    const tagsCopy = {
      ...filterTags,
      [dataIndex]: [],
    };
    let whereFiltersCopy;
    if (dataIndex === 'deliveryType') {
      whereFiltersCopy = {
        ...whereFilter,
        [dataIndex]: null,
      };
    } else {
      whereFiltersCopy = {
        ...whereFilter,
        [dataIndex]: [],
      };
    }
    setFilterVisible(false);
    setFinalFilterTags({ ...filterTags, ...tagsCopy });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFiltersCopy });
    finalWhereFiltersRef.current = {
      ...finalWhereFilters,
      ...whereFiltersCopy,
    };
    setExportFilter({
      ...exportFilter,
      ...finalWhereFilters,
      ...whereFiltersCopy,
    });
    setIsOrderLoading(true);
    setNestedData({});
    setExpandedRowKeys([]);
    orders({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          search: orderSearchTerm,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          orderIds: whereFiltersCopy?.orderNumber,
          deliveryType: whereFiltersCopy?.deliveryType,
          status:
            whereFiltersCopy?.status?.length > 0
              ? whereFiltersCopy?.status
              : statusFilter,
          dateRange: {
            from: whereFiltersCopy?.createdAt?.from?.$d,
            to: whereFiltersCopy?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: whereFiltersCopy?.deliveredOn?.from?.$d,
            to: whereFiltersCopy?.deliveredOn?.to?.$d,
          },
          statusTiming: finalWhereFilters?.statusTiming,
          pincodeIds: whereFiltersCopy?.pincodes,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    paginationPropRef.current = { ...paginationProp, skip: 0, current: 1 };
    clearFilters();
    setFilterVisible(false);
    if (confirm) {
      confirm();
    }
  };

  useEffect(() => {
    if (filterVisible) {
      setFilterList([]);
      setFilterLoading(true);
      setIsEmptyFilterList(false);
      setFilterTags({ ...filterTags, ...finalFilterTags });
      setWhereFilter({ ...whereFilter, ...finalWhereFilters });
      setFilterIsEnd(false);
      const optionsCopy = [];
      const orderStatus = map(
        filter(
          ORDER_STATUS,
          (item) =>
            (isAssignDelPartnerTab
              ? item?.value === ORDER_STATUS?.READY_TO_SHIP?.value
              : item?.value === ORDER_STATUS?.PLACED?.value) ||
            item?.value === ORDER_STATUS?.CONFIRMED?.value,
        ),
        (item) => item,
      );
      switch (filterIndex) {
        case 'deliveryPartnerName':
          users({
            variables: {
              filter: {
                ...initialTableFilter,
                roles: [USER_ROLES?.DELIVERY?.key],
              },
              sort: initialTableSort,
            },
          });
          break;

        case 'users':
          users({
            variables: {
              filter: {
                ...initialTableFilter,
                roles: [USER_ROLES?.USER?.key],
              },
              sort: initialTableSort,
            },
          });
          break;

        case 'pincodes':
          pincodes({
            variables: {
              filter: {
                ...initialTableFilter,
              },
              sort: {
                field: 'pincode',
                order: 'ASC',
              },
            },
          });
          break;

        case 'deliveryType':
          forEach(DELIVERY_TYPE_OPTIONS, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.value });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        case 'orderNumber':
          orderSelect({
            variables: {
              filter: {
                ...initialTableFilter,
                orderType: ORDER_TYPE?.NEW,
              },
              sort: initialTableSort,
            },
          });
          break;

        case 'status':
          forEach(orderStatus, (item) => {
            optionsCopy?.push({ label: item?.text, key: item?.value });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        default:
          break;
      }
    }
    if (!filterVisible) {
      setFilterTags(finalFilterTags);
      setWhereFilter(finalWhereFilters);
      setFilterSearch('');
    }
  }, [filterVisible]);

  const onFilterScroll = (event, dataIndex) => {
    if (dataIndex === 'status') {
      return;
    }
    if (filterScrollDebounceJob) {
      filterScrollDebounceJob?.cancel();
      filterScrollDebounceJob = null;
    }
    if (
      !isUsersLoading &&
      !isPincodesLoading &&
      !isOrderSelectLoading &&
      !filterIsEnd
    ) {
      const target = event?.target;
      const currentLength = filterList?.length;
      if (
        target?.scrollTop + target?.offsetHeight >=
        target?.scrollHeight - 5
      ) {
        filterScrollDebounceJob = debounce(() => {
          setFilterLoading(true);
          switch (dataIndex) {
            case 'deliveryPartnerName':
              users({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                    roles: [USER_ROLES?.DELIVERY?.key],
                  },
                  sort: initialTableSort,
                },
              });
              break;

            case 'users':
              users({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                    roles: [USER_ROLES?.USER?.key],
                  },
                  sort: initialTableSort,
                },
              });
              break;

            case 'pincodes':
              pincodes({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                  },
                  sort: {
                    field: 'pincode',
                    order: 'ASC',
                  },
                },
              });
              break;

            case 'orderNumber':
              orderSelect({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                    orderType: ORDER_TYPE?.NEW,
                  },
                  sort: initialTableSort,
                },
              });
              break;

            default:
              break;
          }
        }, 500);
        filterScrollDebounceJob();
      }
    }
  };

  const handleFilterSearch = (value, dataIndex) => {
    setFilterSearch(value);
    setFilterIsEnd(false);
    setFilterLoading(true);
    setFilterList([]);
    switch (dataIndex) {
      case 'deliveryPartnerName':
        users({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
              roles: [USER_ROLES?.DELIVERY?.key],
            },
            sort: initialTableSort,
          },
        });
        break;

      case 'users':
        users({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
              roles: [USER_ROLES?.USER?.key],
            },
            sort: initialTableSort,
          },
        });
        break;

      case 'pincodes':
        pincodes({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
            },
            sort: {
              field: 'pincode',
              order: 'ASC',
            },
          },
        });
        break;

      case 'orderNumber':
        orderSelect({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
              orderType: ORDER_TYPE?.NEW,
            },
            sort: initialTableSort,
          },
        });
        break;

      default:
        break;
    }
  };

  const changeFilter = (newFilterObj, dataIndex) => {
    const {
      target: { value },
    } = newFilterObj;

    const filterTagsCopy = { ...filterTags };
    let newTagList;
    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filter(filterTagsCopy?.[dataIndex], (item) => {
          if (item?.label) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    } else {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filterTagsCopy?.[dataIndex]
          ? [...filterTagsCopy?.[dataIndex], value]
          : [value],
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const handleDeleteFilter = (value, dataIndex) => {
    let newTagList;

    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTags,
        [dataIndex]: filter(filterTags?.[dataIndex], (item) => {
          if (item?.key) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const onRangePickerChange = (values, dataIndex) => {
    const rangeObj = {
      from: values[0]?.startOf('day'),
      to: values?.[1]?.endOf('day'),
    };

    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: rangeObj,
    };
    if (!values?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const onRadioChange = (value, dataIndex) => {
    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: value,
    };
    if (!value?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const filterPopup = (dataIndex, showSearch = true) => ({
    filterDropdown: ({ confirm, clearFilters }) => {
      if (dataIndex === 'createdAt' || dataIndex === 'deliveredOn') {
        return (
          <div className="custom-filter-dropdown">
            <RangePicker
              allowClear={false}
              format={DATE_FORMATS?.REGULAR}
              onChange={(values) => onRangePickerChange(values, dataIndex)}
              value={
                whereFilter?.[dataIndex]?.from
                  ? [
                      whereFilter?.[dataIndex]?.from,
                      whereFilter?.[dataIndex]?.to,
                    ]
                  : null
              }
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      if (dataIndex === 'deliveryType') {
        return (
          <div className="custom-filter-dropdown">
            <Group
              options={DELIVERY_TYPE_OPTIONS}
              value={whereFilter?.[dataIndex]}
              onChange={(e) => onRadioChange(e?.target?.value, dataIndex)}
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="custom-filter-dropdown">
          <LoaderComponent
            spinning={filterLoading}
            setHeight={35}
            size="medium"
          >
            {showSearch && (
              <SearchComponent
                className="filter-search"
                id="search-container-id-roles"
                placeholder="Search..."
                name={dataIndex}
                query={filterSearch}
                setQuery={setFilterSearch}
                getData={(value) => handleFilterSearch(value, dataIndex)}
              />
            )}
            {filterTags?.[dataIndex]?.length > 0 && (
              <div className="filter-tags-wrapper">
                {map(filterTags?.[dataIndex], (item) => (
                  <Tag
                    key={item?.key}
                    closable
                    onClose={() => handleDeleteFilter(item, dataIndex)}
                    className="filter-tag"
                  >
                    <span>{item?.label}</span>
                  </Tag>
                ))}
              </div>
            )}
            <div
              className="filter-checkboxes"
              onScroll={(e) => onFilterScroll(e, dataIndex)}
            >
              {filterList?.length > 0
                ? map(filterList, (item) => (
                    <div className="single-checkbox-div" key={item?.key}>
                      <Checkbox
                        value={item}
                        checked={whereFilter?.[dataIndex]?.includes(item?.key)}
                        key={item?.key || item}
                        onChange={(e) => changeFilter(e, dataIndex)}
                        className="single-checkbox"
                      >
                        <span>{item?.label}</span>
                      </Checkbox>
                    </div>
                  ))
                : isEmptyFilterList && (
                    <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
                  )}
            </div>
          </LoaderComponent>
          <Divider className="divider-filter" />
          <div className="d-flex justify-center">
            <Button
              size="small"
              className="reset-button"
              id="roles-filter-reset"
              onClick={() => handleReset(clearFilters, dataIndex, confirm)}
            >
              Reset
            </Button>
            <Button
              size="small"
              className="common-button ok-button"
              id="roles-filter-ok"
              type="primary"
              onClick={() => getFilterData(confirm, dataIndex)}
            >
              Ok
            </Button>
          </div>
        </div>
      );
    },
    filterIcon: () =>
      finalWhereFilters?.[dataIndex]?.length > 0 ||
      !isEmpty(finalWhereFilters?.[dataIndex]) ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (isVisible) => {
      setFilterIndex(dataIndex);
      setFilterVisible(isVisible);
    },
  });

  const handleExpand = async (expanded, record) => {
    setIsLoading({
      [record?.id]: true,
    });
    setIsDisabled(true);
    if (!nestedData?.[record?.id]) {
      const res = await orderItems({
        variables: {
          where: {
            id: record?.id,
          },
        },
      });
      if (res?.data) {
        setNestedData((state) => ({
          ...state,
          [record?.id]: res?.data,
        }));
      }
      setIsDisabled(false);
    }
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, record?.id]);
    } else {
      setExpandedRowKeys(expandedRowKeys?.filter((key) => key !== record?.id));
    }
    setIsLoading({
      [record?.id]: false,
    });
    setIsDisabled(false);
  };

  const handleOrderChange = (value, record) => {
    setOrder(record);
    setIsOrderAction(true);
    if (value === FULFILLMENT_STATUS?.FULFILLED?.value) {
      setIsReadyPrompts(true);
    } else {
      setIsAddCommentModalOpen(true);
    }
  };

  const handleOrderItemChange = (value, record, orderDetails) => {
    setOrderItem(record);
    setOrder(orderDetails);
    setIsOrderAction(false);
    if (value === FULFILLMENT_STATUS?.FULFILLED?.value) {
      setIsFulfillPrompts(true);
    } else {
      setIsAddCommentModalOpen(true);
    }
  };

  const hasSelected = selectedRowKeys.length > 0;
  const renderBlinkingDotEl = (orderStatus) => {
    if (isAssignDelPartnerTab && orderStatus === ORDER_STATUS.CONFIRMED.value) {
      return (
        <Badge status="processing" color="red" className="custom-badge mr-8" />
      );
    }
    if (!isAssignDelPartnerTab && orderStatus === ORDER_STATUS.PLACED.value) {
      return (
        <Badge status="processing" color="red" className="custom-badge mr-8" />
      );
    }
    return null;
  };

  const renderSLA = (record) => {
    if (isAssignDelPartnerTab) {
      if (record?.status === ORDER_STATUS.READY_TO_SHIP.value) {
        return <div>N/A</div>;
      }

      if (record?.nextStatusTime) {
        return (
          <div>
            {formatDate(record?.nextStatusTime, DATE_FORMATS?.WITH_TIME)}
          </div>
        );
      }
    } else if (record?.nextStatusTime) {
      return (
        <div>{formatDate(record?.nextStatusTime, DATE_FORMATS?.WITH_TIME)}</div>
      );
    }
  };
  const columns = [
    {
      title: 'ORDER ID',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'left',
      ellipsis: true,
      className: 'max-width-column',
      sorter: true,
      width: 150,
      sortOrder: sortedInfo?.columnKey === 'createdAt' && sortedInfo?.order,
      render: (_, record) => (
        <div className="d-flex align-center">
          {record?.orderNumber}
          {record?.isFirstOrder && (
            <Tag color="green" className="ml-8 mr-0">
              {FIRST_ORDER_FLAG}{' '}
            </Tag>
          )}
          {record?.delayedInConfirmed && (
            <Tag color="red" className="ml-8">
              Delayed
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'QUANTITY',
      dataIndex: 'quantity',
      key: 'quantity',
      ellipsis: true,
      align: 'left',
      width: 100,
      className: 'max-width-column',
      render: (_, record) => record?.orderItemCount || '-',
    },
    {
      title: 'TOTAL ORDERS COUNT',
      dataIndex: 'totalOrdersCount',
      key: 'totalOrdersCount',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === 'totalOrdersCount' && sortedInfo?.order,
      render: (_, record) => record?.totalOrdersCount ?? '-',
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <Badge
          color={ORDER_STATUS?.[record?.status]?.color}
          text={ORDER_STATUS?.[record?.status]?.text}
        />
      ),
      ...(isAssignDelPartnerTab ? filterPopup('status', false) : {}),
    },
    {
      title: isAssignDelPartnerTab
        ? 'RIDER ASSIGNMENT SLA'
        : 'ORDER PACKAGING SLA',
      dataIndex: 'orderPackagingSLA',
      key: 'orderPackagingSLA',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) => <div>{renderSLA(record)}</div>,
    },
    {
      title: isAssignDelPartnerTab
        ? 'RIDER ASSIGNMENT TIME LEFT'
        : 'ORDER PACKAGING TIME LEFT',
      dataIndex: 'timeLeft',
      key: 'timeLeft',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) => {
        const nextStatusTime = new Date(record?.nextStatusTime)?.getTime();
        return (
          <div>
            {
              // eslint-disable-next-line no-nested-ternary
              isAssignDelPartnerTab ? (
                record?.status !== ORDER_STATUS?.CONFIRMED.value ? (
                  'N/A' // or whatever you want to show for non-confirmed statuses
                ) : (
                  <Countdown date={nextStatusTime} renderer={renderer} />
                )
              ) : !isEmpty(record?.nextStatusTime) ? (
                <Countdown date={nextStatusTime} renderer={renderer} />
              ) : (
                'N/A'
              )
            }
          </div>
        );
      },
    },
    {
      title: 'DELIVERY SLA',
      dataIndex: 'deliverySLA',
      key: 'deliverySLA',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) =>
        record?.nextStatusTime &&
        record?.status === ORDER_STATUS?.READY_TO_SHIP.value
          ? formatDate(record?.nextStatusTime, DATE_FORMATS?.WITH_TIME)
          : 'N/A',
    },
    {
      title: 'DELIVERY TIME LEFT',
      dataIndex: 'deliveryTimeLeft',
      key: 'deliveryTimeLeft',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) => {
        const nextStatusTime = new Date(record.nextStatusTime)?.getTime();
        return (
          <div>
            {!isEmpty(record?.nextStatusTime) &&
            record?.status === ORDER_STATUS?.READY_TO_SHIP.value ? (
              <Countdown date={nextStatusTime} renderer={renderer} />
            ) : (
              'N/A'
            )}
          </div>
        );
      },
    },
    {
      title: 'DELIVERY TYPE',
      dataIndex: 'deliveryType',
      key: 'deliveryType',
      ellipsis: true,
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) =>
        DELIVERY_TYPE?.[record?.deliveryType]?.label || '-',
      ...filterPopup('deliveryType'),
    },
    {
      title: 'USER NAME',
      dataIndex: 'userName',
      key: 'userName',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: `max-width-column`,
      onCell: (record) => ({
        onClick: () =>
          isUserPermission &&
          record?.user?.id &&
          history?.push(
            `${ROUTES?.SYSTEM_USERS}${ROUTES?.END_USERS}${ROUTES?.BASIC_DETAILS}/view/${record?.user?.id}/${record?.user?.userNumber}`,
          ),
        className: `${
          isUserPermission && record?.user?.id && 'pointer table-cell'
        }`,
      }),
      render: (_, record) => {
        const userData = record?.user;
        return (
          <>
            {userData?.firstName || userData?.lastName ? (
              <div className="table-data-with-id">{`${
                userData?.firstName ? userData?.firstName : ''
              } ${userData?.lastName ? userData?.lastName : ''}`}</div>
            ) : (
              '-'
            )}
            {userData?.userNumber ? (
              <div className="font-size-12">({userData?.userNumber})</div>
            ) : (
              ''
            )}
          </>
        );
      },
    },

    {
      title: 'PINCODE',
      dataIndex: 'pincode',
      key: 'pincode',
      ellipsis: true,
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) => record?.pincode?.pincode || '-',
      ...filterPopup('pincodes'),
    },
    {
      title: 'DELAY REASON',
      dataIndex: 'delayReason',
      key: 'delayReason',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <div className="action-button" onClick={(e) => e?.stopPropagation()}>
          {!isEmpty(record?.metadata) ? (
            <Tooltip title="View reason" placement="top" zIndex={4}>
              <CommentIcon
                className="mr-16 pointer svg-icon"
                onClick={() => {
                  setIsReasonPrompt(true);
                  setOrder(record);
                  setIsOrderAction(true);
                }}
              />
            </Tooltip>
          ) : (
            <span className="mr-24">-</span>
          )}
        </div>
      ),
    },

    {
      title: 'DELIVERY PARTNER NAME',
      dataIndex: 'deliveryPartnerName',
      key: 'deliveryPartnerName',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      onCell: (record) => ({
        onClick: () =>
          isDeliveryPartnerPermission &&
          record?.deliveryPartner?.id &&
          history?.push(
            `${ROUTES?.SYSTEM_USERS}${ROUTES?.DELIVERY_PARTNERS}/view/${record?.deliveryPartner?.id}`,
          ),
        className: `${
          isUserPermission &&
          record?.deliveryPartner?.id &&
          'pointer table-cell'
        }`,
      }),
      render: (_, record) => (
        <>
          {record?.deliveryPartner?.firstName ||
          record?.deliveryPartner?.lastName ? (
            <div className="table-data-with-id">{`${
              record?.deliveryPartner?.firstName
                ? record?.deliveryPartner?.firstName
                : ''
            } ${
              record?.deliveryPartner?.lastName
                ? record?.deliveryPartner?.lastName
                : ''
            }`}</div>
          ) : (
            '-'
          )}
          {record?.deliveryPartner?.userNumber ? (
            <div className="font-size-12">
              ({record?.deliveryPartner?.userNumber})
            </div>
          ) : (
            ''
          )}
        </>
      ),
    },

    {
      title: 'PLACED ON',
      dataIndex: 'placedOn',
      key: 'placedOn',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.WITH_TIME),
      ...filterPopup('createdAt'),
    },
    {
      title: 'ESTIMATED SHIPPING DATE',
      dataIndex: 'deliveredOn',
      key: 'deliveredOn',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.deliveredOn, DATE_FORMATS?.REGULAR) || '-',
      ...filterPopup('deliveredOn'),
    },

    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      width: 250,
      render: (_, record) => {
        if (isAssignDelPartnerTab) {
          const { firstName, lastName, id, userNumber } =
            record?.deliveryPartner || {};
          const assignedDeliveryPartner = {
            firstName,
            lastName,
            id,
            userNumber,
          };
          return (
            <div onClick={(e) => e?.stopPropagation()}>
              <UserSelect
                isLibrary
                size="small"
                className="user-select"
                placeholder="Select Delivery Partner"
                showAction={['click']}
                value={id || null}
                userData={id ? assignedDeliveryPartner : null}
                roles={USER_ROLES?.DELIVERY?.key}
                onChange={(value, userData) => {
                  setOrder(record);
                  setDeliveryPartnerData(userData);
                  setIsDeliveryPartnerPrompts(true);
                }}
                allowClear={false}
              />
            </div>
          );
        }
        let selectedValue = null;
        if (
          record?.status === ORDER_STATUS?.CANCELLED?.value ||
          record?.status === ORDER_STATUS?.CONFIRMED?.value
        ) {
          if (record?.status === ORDER_STATUS?.CANCELLED?.value) {
            selectedValue = FULFILLMENT_STATUS?.UNFULFILLED?.value;
          } else {
            selectedValue = FULFILLMENT_STATUS?.FULFILLED?.value;
          }
        } else {
          selectedValue = null;
        }
        return (
          <div onClick={(e) => e?.stopPropagation()}>
            <SelectComponent
              size="small"
              className="full-width"
              placeholder="Select Action"
              showAction={['click']}
              value={selectedValue}
              showSearch={false}
              disabled={
                !!selectedValue ||
                (record.transferOrderItemCount !== 0 &&
                  record.transferOrderItemCount !== null)
              }
              options={NEW_ORDER_TABLE_ACTIONS_OPTIONS}
              onSelect={(value) => handleOrderChange(value, record)}
              allowClear={false}
            />
          </div>
        );
      },
    },
  ]?.filter((item) => {
    if (
      !isAssignDelPartnerTab &&
      (item?.dataIndex === 'deliveryPartnerName' ||
        item?.dataIndex === 'deliveryTimeLeft' ||
        item?.dataIndex === 'deliverySLA')
    ) {
      return item?.hidden;
    }

    if (isAssignDelPartnerTab && item?.dataIndex === 'delayReason') {
      return item?.hidden;
    }

    if (isAssignDelPartnerTab && item?.dataIndex === 'totalOrdersCount') {
      return item?.hidden;
    }
    return !item?.hidden;
  });
  const expandedRowRender = (data) => {
    const orderItemList = nestedData?.[data?.id]?.orderItems?.data;
    const nestedColumns = [
      {
        title: 'ORDER ID',
        dataIndex: 'orderNumber',
        key: 'orderNumber',
        ellipsis: true,
        align: 'left',
        fixed: 'left',
        className: 'max-width-column',
        width: 100,
        render: (_, record) => <>{record?.order?.orderNumber || '-'}</>,
      },
      {
        title: 'PRODUCT NAME',
        dataIndex: 'productName',
        key: 'productName',
        ellipsis: true,
        align: 'left',
        fixed: 'left',
        width: 250,
        className: 'max-width-column',
        onCell: (record) => ({
          onClick: () =>
            isToyPermission &&
            record?.toy?.id &&
            history?.push(
              `${ROUTES?.MASTER_LISTS}${ROUTES?.TOY}/view/${record?.toy?.id}`,
            ),
          className: `${
            isToyPermission && record?.toy?.id && 'pointer table-cell'
          }`,
        }),
        render: (_, record) => (
          <>
            <div className="table-data-with-id">{record?.toy?.name || '-'}</div>
            {record?.toy?.toyNumber && (
              <div className="font-size-12">({record?.toy?.toyNumber})</div>
            )}
          </>
        ),
      },
      {
        title: 'IMAGES',
        dataIndex: 'images',
        key: 'images',
        ellipsis: true,
        width: 100,
        align: 'left',
        className: 'max-width-column',
        fixed: 'left',
        render: (_, record) => {
          const images = record?.toy?.images;
          return (
            <span>
              {images?.length > 0 ? (
                <>
                  <Image
                    preview={{
                      visible: false,
                    }}
                    width={20}
                    height={20}
                    src={images?.[0]?.url}
                    onClick={(e) => {
                      e?.stopPropagation();
                      setVisible(true);
                      setToyImages(images);
                    }}
                    className="pointer"
                  />
                </>
              ) : (
                <span>No Images</span>
              )}
              {images?.length > 1 ? ` + ${images?.length - 1}` : ''}
            </span>
          );
        },
      },
      {
        title: 'BRAND',
        dataIndex: 'brand',
        key: 'brand',
        align: 'left',
        width: 300,
        className: 'max-width-column',
        render: (_, record) => record?.toy?.brand?.name || '-',
      },
      {
        title: 'PACKAGING MATERIAL',
        dataIndex: 'packagingMaterial',
        key: 'packagingMaterial',
        ellipsis: true,
        width: 250,
        align: 'left',
        className: 'max-width-column',
        render: (_, record) => (
          <>
            {record?.toy?.bag ? (
              <div>
                <span className="mr-8">
                  {record?.toy?.bag?.name} ({record?.toy?.bag?.size})
                </span>
                <Image
                  preview={{ visible: false }}
                  width={22}
                  src={record?.toy?.bag?.image}
                  onClick={(e) => {
                    e?.stopPropagation();
                    setPreviewVisible(true);
                    setToyPreviewImage(record?.toy?.bag?.image);
                  }}
                  className="pointer"
                />
              </div>
            ) : (
              '-'
            )}
          </>
        ),
      },
      {
        title: 'AGE GROUPS',
        dataIndex: 'ageGroups',
        key: 'ageGroups',
        align: 'left',
        width: 300,
        className: 'max-width-column',
        render: (_, record) =>
          record?.toy?.ageGroups?.map((ageGroup) =>
            ageGroup?.minAge > 18 ? (
              <Tag key={ageGroup?.id}>
                {Math.floor(ageGroup?.minAge / 12)} -{' '}
                {Math.floor(ageGroup?.maxAge / 12)} Years
              </Tag>
            ) : (
              <Tag key={ageGroup?.id}>
                {ageGroup?.minAge} - {ageGroup?.maxAge} Months
              </Tag>
            ),
          ),
      },
      {
        title: 'FULFILLMENT STATUS',
        dataIndex: 'fulfillmentStatus',
        key: 'fulfillmentStatus',
        ellipsis: true,
        align: 'left',
        width: 300,
        className: 'max-width-column',
        render: (_, record) =>
          record?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
          record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
          record?.status === FULFILLMENT_STATUS?.CANCELLED?.value ? (
            <Badge
              color={FULFILLMENT_STATUS?.[record?.status]?.color}
              text={FULFILLMENT_STATUS?.[record?.status]?.text}
            />
          ) : (
            '-'
          ),
      },
      {
        title: 'EXPECTED RETURN',
        dataIndex: 'returnDate',
        key: 'returnDate',
        ellipsis: true,
        align: 'left',
        width: 200,
        className: 'max-width-column',
        render: (_, record) =>
          formatDate(record?.returnDate, DATE_FORMATS?.REGULAR) || '-',
      },
      {
        title: 'NOTE',
        dataIndex: 'note',
        key: 'note',
        ellipsis: true,
        align: 'left',
        width: 300,
        className: 'max-width-column',
        render: (_, record) => (
          <div className="action-button">
            {record?.cancellationData?.type ===
            CANCELLATION_TYPE?.LIBRARY?.value ? (
              <Tooltip title="View comment" placement="top" zIndex={4}>
                <CommentIcon
                  className="mr-24 pointer svg-icon"
                  onClick={() => {
                    setIsNotePrompts(true);
                    setOrderItem(record);
                    setIsOrderAction(false);
                  }}
                />
              </Tooltip>
            ) : (
              <span className="mr-24">-</span>
            )}
          </div>
        ),
      },
      {
        title: 'ACTIONS',
        dataIndex: 'actions',
        key: 'actions',
        width: 250,
        render: (_, record) => (
          <SelectComponent
            size="small"
            className="full-width"
            placeholder="Select Action"
            showSearch={false}
            value={
              record?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
              record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value
                ? record?.status
                : null
            }
            disabled={
              record?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
              record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
              record?.status === FULFILLMENT_STATUS?.CANCELLED?.value ||
              (record?.transferOrders?.length > 0 &&
                record?.transferOrders?.[0]?.status !==
                  TRANSFER_ORDER_STATUS.RECEIVED.value)
            }
            options={NEW_ORDER_EXPANDED_TABLE_ACTIONS_OPTIONS}
            onChange={(value, e) => {
              handleOrderItemChange(value, record, data);
            }}
            allowClear={false}
          />
        ),
      },
    ]?.filter((item) => {
      if (isAssignDelPartnerTab && item?.dataIndex === 'actions') {
        return item?.hidden;
      }
      return !item?.hidden;
    });

    return (
      <CommonTable
        loading={isLoading[data?.id]}
        columns={nestedColumns}
        dataSource={orderItemList || []}
        pagination={false}
        rowKey={(record) => record?.id}
        className="nested-table colored-nested-table"
      />
    );
  };

  const expandIcon = ({ expanded, onExpand, record }) =>
    expanded ? (
      <button
        type="button"
        className="ant-table-row-expand-icon ant-table-row-expand-icon-expanded disabled"
        aria-label="Expand row"
        aria-expanded="false"
        onClick={(e) => onExpand(record, e)}
        disabled={isDisabled}
      />
    ) : (
      <button
        type="button"
        className="ant-table-row-expand-icon ant-table-row-expand-icon-collapsed"
        aria-label="Collapse row"
        aria-expanded="true"
        onClick={(e) => onExpand(record, e)}
        disabled={isDisabled}
      />
    );

  const handleCancel = () => {
    setIsContentMasterModalOpen(false);
    setOrderItem({});
  };

  const locale = {
    emptyText: isEmptyOrderList ? '' : <span />,
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => ({
      disabled: isAssignDelPartnerTab
        ? false
        : record?.status === ORDER_STATUS?.CANCELLED?.value ||
          record?.status === ORDER_STATUS?.CONFIRMED?.value ||
          record?.delayedInConfirmed,
    }),
  };

  const handleFilter = (values) => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsOrderLoading(true);
    setIsAllFilterModalOpen(false);
    setOrderList([]);
    setIsActive(true);
    const filters = {
      status: values?.status,
      createdAt: values?.createdAt?.[1]
        ? { from: values?.createdAt?.[0], to: values?.createdAt?.[1] }
        : null,
      deliveredOn: values?.deliveredOn?.[1]
        ? { from: values?.deliveredOn?.[0], to: values?.deliveredOn?.[1] }
        : null,
      pincodes: values?.pincodes,
      deliveryType: values?.deliveryType,
    };
    setWhereFilter(filters);
    setFinalWhereFilters(filters);
    orders({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: ORDER_TYPE?.NEW,
          search: orderSearchTerm,
          deliveryType: values?.deliveryType,
          status: values?.status?.length > 0 ? values?.status : statusFilter,
          dateRange: {
            from: values?.createdAt?.[0],
            to: values?.createdAt?.[1],
          },
          deliveredOn: {
            from: values?.deliveredOn?.[0],
            to: values?.deliveredOn?.[1],
          },
          pincodeIds: values?.pincodes,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  };
  const handleSelectAllMobile = (checked) => {
    if (checked) {
      if (!isAssignDelPartnerTab) {
        setSelectedRowKeys((prev) => {
          const newIds = orderList
            ?.filter((record) =>
              [
                ORDER_STATUS?.READY_TO_SHIP?.value,
                ORDER_STATUS?.SHIPPED?.value,
                ORDER_STATUS?.CONFIRMED?.value,
                ORDER_STATUS?.DELIVERED?.value,
              ]?.includes(record?.status),
            )
            ?.map((data) => data?.id);

          return Array.from(new Set([...prev, ...newIds]));
        });
      } else {
        setSelectedRowKeys((prev) => {
          const newIds = orderList
            ?.filter((record) =>
              [
                ORDER_STATUS?.READY_TO_SHIP?.value,
                ORDER_STATUS?.CONFIRMED?.value,
              ]?.includes(record?.status),
            )
            ?.map((data) => data?.id);

          return Array.from(new Set([...prev, ...newIds]));
        });
      }
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleSelectOrders = (key) => {
    setSelectedRowKeys((prev) => {
      if (prev?.includes(key)) {
        return prev?.filter((item) => item !== key);
      }
      return [...prev, key];
    });
  };

  const findSelectableOrders = () => {
    if (!isAssignDelPartnerTab) {
      return orderList?.filter((record) =>
        [ORDER_STATUS?.PLACED?.value]?.includes(record?.status),
      );
    }
    return orderList?.filter(
      (record) =>
        [ORDER_STATUS?.CONFIRMED?.value]?.includes(record?.status) ||
        [ORDER_STATUS?.READY_TO_SHIP?.value]?.includes(record?.status),
    );
  };

  const isSelectAllDisabled = () => {
    if (isAssignDelPartnerTab) return false;

    return orderList?.some(
      (record) =>
        [
          ORDER_STATUS?.CANCELLED?.value,
          ORDER_STATUS?.CONFIRMED?.value,
        ]?.includes(record?.status) || record?.delayedInConfirmed,
    );
  };
  return (
    <>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isDeliveryPartnerPrompts}
        onOk={() =>
          handleAssignDeliveryPartner([order?.id], deliveryPartnerData?.value)
        }
        onCancel={() => setIsDeliveryPartnerPrompts(false)}
        okButtonProps={{ loading: isDeliveryPartnerLoading }}
      >
        Are you sure you want to assign
        <strong> {deliveryPartnerData?.children} </strong>?
      </Modal>

      <CommonPreviewTable
        visible={visible}
        setVisible={setVisible}
        images={toyImages}
        setImages={setToyImages}
      />
      <CommonPreview
        visible={previewVisible}
        setVisible={setPreviewVisible}
        image={toyPreviewImage}
        setImage={setToyPreviewImage}
      />

      <AssignDeliveryPartnerModal
        form={assignDelPartnerForm}
        onFinish={(values) =>
          handleAssignDeliveryPartner(selectedRowKeys, values?.name)
        }
        loadings={isDeliveryPartnerLoading}
        isModalOpen={isDeliveryPartnerModalOpen}
        setIsModalOpen={setDeliveryPartnerModalOpen}
        title="Assign Delivery Partner"
        isLibrary
      />
      <div className="d-flex justify-end align-center mb-16">
        <OrderStatusSelect
          finalWhereFiltersRef={finalWhereFiltersRef}
          finalWhereFilters={finalWhereFilters}
          setFinalWhereFilters={setFinalWhereFilters}
          fetchOrders={fetchOrders}
          setIsOrderLoading={setIsOrderLoading}
        />
        {isAssignDelPartnerTab ? (
          <Button
            type="primary"
            disabled={!hasSelected}
            className="mr-8"
            onClick={() => setDeliveryPartnerModalOpen(true)}
          >
            Assign Rider
          </Button>
        ) : (
          <Button
            type="primary"
            disabled={!hasSelected}
            className="mr-8"
            onClick={() => setIsConfirmPrompts(true)}
          >
            Confirm Orders
          </Button>
        )}
        {!isDesktop && (
          <Badge dot={hasNonEmptyValueObj(finalWhereFilters)} size="small">
            <FilterIcon
              className="mobile-svg-icon ml-16"
              onClick={() => setIsAllFilterModalOpen(true)}
            />
          </Badge>
        )}
        {isDesktop && (
          <SearchComponent
            className="search-box"
            query={orderSearchTerm}
            setQuery={setOrderSearchTerm}
            orderSearchTermRef={orderSearchTermRef}
            getData={handleSearch}
          />
        )}
      </div>

      {!isDesktop && (
        <div className="d-flex justify-end align-center">
          <SearchComponent
            className="search-box"
            query={orderSearchTerm}
            setQuery={setOrderSearchTerm}
            orderSearchTermRef={orderSearchTermRef}
            getData={handleSearch}
          />
          <Checkbox
            indeterminate={
              selectedRowKeys?.length < findSelectableOrders()?.length &&
              selectedRowKeys?.length > 0
            }
            disabled={isSelectAllDisabled()}
            checked={
              selectedRowKeys?.length > 0 &&
              selectedRowKeys?.length === findSelectableOrders()?.length &&
              !isOrderLoading &&
              !isFetchMoreOrderLoading
            }
            onChange={(e) => {
              handleSelectAllMobile(e?.target?.checked);
            }}
            className="ml-16"
          >
            Select all
          </Checkbox>

          <div className="ml-16 d-flex align-center">
            <SortDropdown
              isOpen={isAllSortPopoverOpen}
              setIsOpen={setIsAllSortPopoverOpen}
              options={map(sort, (item) => item)}
              sortedInfo={sortedInfo}
              setSortedInfo={setSortedInfo}
              onFinish={handleSort}
            />
          </div>
        </div>
      )}
      {isDesktop ? (
        <div className="colored-table">
          <CommonTable
            rowClassName={(record) =>
              getRowClassName(
                record,
                thresholdData,
                playNotificationSound,
                criticalOrdersRef,
              )
            }
            locale={locale}
            columns={columns}
            data={orderList || []}
            loading={isOrderLoading}
            rowSelection={rowSelection}
            onChange={handleTableChange}
            paginationConfig={paginationProp}
            rowKey={(record) => record?.id}
            expandedRowKeys={expandedRowKeys}
            onExpand={(_, record) => setOrder(record)}
            onRow={() => ({
              style: { cursor: 'pointer' },
            })}
            expandable={{
              onExpand: (expanded, record) => handleExpand(expanded, record),
              expandedRowRender: (record) => expandedRowRender(record),
              expandIcon,
              expandRowByClick: true,
            }}
          />
        </div>
      ) : (
        <div className="mobile-container-search-action no-scroll-bar">
          <AllFilterOrderModal
            onFinish={handleFilter}
            isModalOpen={isAllFilterModalOpen}
            setIsModalOpen={setIsAllFilterModalOpen}
            finalWhereFilters={finalWhereFilters}
            setFinalWhereFilters={setFinalWhereFilters}
            isNewPath={!isAssignDelPartnerTab}
            hideStatus={!isAssignDelPartnerTab}
            isAssignDelPartnerTab={isAssignDelPartnerTab}
          />
          <AllOrderItemModal
            order={order}
            isNewPath={!isAssignDelPartnerTab}
            setOrder={setOrder}
            isModalOpen={isAllOrderItemModalOpen}
            setIsModalOpen={setIsAllOrderItemModalOpen}
            handleExpand={handleExpand}
            nestedData={nestedData}
            isLoading={isLoading}
            setIsNotePrompts={setIsNotePrompts}
            setOrderItem={setOrderItem}
            setIsOrderAction={setIsOrderAction}
            handleOrderItemChange={handleOrderItemChange}
            setIsContentMasterModalOpen={setIsContentMasterModalOpen}
            setVisible={setVisible}
            setToyImages={setToyImages}
            isAssignDelPartnerTab={isAssignDelPartnerTab}
          />
          <AllOrderList
            orderList={orderList}
            infiniteScrollRef={infiniteScrollRef}
            isFetchMoreOrderLoading={isFetchMoreOrderLoading}
            isEmptyOrderList={isEmptyOrderList}
            isOrderLoading={isOrderLoading}
            setOrder={setOrder}
            isNewPath={!isAssignDelPartnerTab}
            setIsNotePrompts={setIsNotePrompts}
            setIsOrderAction={setIsOrderAction}
            handleOrderChange={handleOrderChange}
            setIsAllOrderItemModalOpen={setIsAllOrderItemModalOpen}
            setVisible={setVisible}
            setToyImages={setToyImages}
            handleSelectOrders={handleSelectOrders}
            selectedOrders={selectedRowKeys}
            setIsReasonPrompt={setIsReasonPrompt}
            isAssignDelPartnerTab={isAssignDelPartnerTab}
            setDeliveryPartnerData={setDeliveryPartnerData}
            setIsDeliveryPartnerPrompts={setIsDeliveryPartnerPrompts}
            isLibrary
          />
        </div>
      )}
    </>
  );
};

export default RiderManagementTable;
